<template>
  <Teleport to="body">
    <Transition name="toast-fade">
      <div
        v-if="visible"
        class="toast-container"
        :class="`toast-${type}`"
        role="alert"
        aria-live="polite"
        aria-atomic="true"
      >
        <div class="toast-content">
          <span class="toast-icon" aria-hidden="true">{{ iconForType }}</span>
          <span class="toast-message">{{ message }}</span>
        </div>
        <button
          v-if="dismissible"
          class="toast-close"
          @click="close"
          aria-label="Benachrichtigung schließen"
        >
          ×
        </button>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';

const props = defineProps({
  message: {
    type: String,
    required: true,
  },
  type: {
    type: String,
    default: 'info', // 'success', 'error', 'warning', 'info'
    validator: (value) => ['success', 'error', 'warning', 'info'].includes(value),
  },
  duration: {
    type: Number,
    default: 3000, // 3 seconds
  },
  dismissible: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['close']);

const visible = ref(false);
let timeout = null;

const iconForType = computed(() => {
  const icons = {
    success: '✓',
    error: '✕',
    warning: '⚠',
    info: 'ℹ',
  };
  return icons[props.type] || icons.info;
});

const close = () => {
  visible.value = false;
  if (timeout) {
    clearTimeout(timeout);
  }
  emit('close');
};

onMounted(() => {
  visible.value = true;

  if (props.duration > 0) {
    timeout = setTimeout(() => {
      close();
    }, props.duration);
  }
});

watch(() => props.message, () => {
  // Reset timer when message changes
  if (timeout) {
    clearTimeout(timeout);
  }
  if (props.duration > 0) {
    timeout = setTimeout(() => {
      close();
    }, props.duration);
  }
});
</script>

<style scoped>
.toast-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  min-width: 300px;
  max-width: 500px;
  padding: 1rem 1.5rem;
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  background: white;
  border-left: 4px solid;
}

.toast-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
}

.toast-icon {
  font-size: 1.25rem;
  font-weight: bold;
  flex-shrink: 0;
}

.toast-message {
  color: #333;
  line-height: 1.5;
}

.toast-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  line-height: 1;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
  flex-shrink: 0;
}

.toast-close:hover {
  background: rgba(0, 0, 0, 0.05);
  color: #333;
}

/* Type-specific styles */
.toast-success {
  border-left-color: #10b981;
}

.toast-success .toast-icon {
  color: #10b981;
}

.toast-error {
  border-left-color: #ef4444;
}

.toast-error .toast-icon {
  color: #ef4444;
}

.toast-warning {
  border-left-color: #f59e0b;
}

.toast-warning .toast-icon {
  color: #f59e0b;
}

.toast-info {
  border-left-color: #3b82f6;
}

.toast-info .toast-icon {
  color: #3b82f6;
}

/* Animation */
.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: all 0.3s ease;
}

.toast-fade-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.toast-fade-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .toast-container {
    top: 10px;
    right: 10px;
    left: 10px;
    min-width: auto;
    max-width: none;
  }
}
</style>
